import { DestroyRef, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { isSupported, logEvent } from 'firebase/analytics';
import { switchMap } from 'rxjs';
import { SCREEN_VIEW_EVENT, screenViewEvent } from './screen-view-event';
import { Analytics } from './tokens';
import { UserTrackingService } from './user-tracking.service';

/**
 * Subscribes to the Router and logs `screen_view` events to Firebase
 * Analytics. Activated by registering it as a class provider alongside
 * `provideAnalytics` plus an environment-initializer that forces injection:
 *
 *   provideAnalytics(...),
 *   ScreenTrackingService,
 *   provideEnvironmentInitializer(() => inject(Analytics)),
 *
 * Ported from `@angular/fire/analytics` — preserves upstream's screen_class,
 * multi-outlet groupBy, and previous-screen pairwise logic. See
 * `screen-view-event.ts` for the event-payload builder.
 */
@Injectable({ providedIn: 'root' })
export class ScreenTrackingService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router, { optional: true });
  private readonly title = inject(Title, { optional: true });
  private readonly analytics = inject(Analytics, { optional: true });
  private readonly userTracking = inject(UserTrackingService, { optional: true });
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    if (!isPlatformBrowser(this.platformId) || !this.router || !this.analytics) {
      return;
    }
    const router = this.router;
    const analytics = this.analytics;
    const userTracking = this.userTracking;

    // `isSupported()` resolves false in environments without measurement
    // (some SSR contexts, locked-down iframes). Gating the subscription on
    // it matches upstream and avoids gtag.js errors in those environments.
    isSupported().then((supported) => {
      if (!supported) return;
      screenViewEvent(router, this.title)
        .pipe(
          switchMap(async (params) => {
            if (userTracking) {
              await userTracking.initialized;
            }
            return logEvent(analytics, SCREEN_VIEW_EVENT, params);
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    });
  }
}
